import React, { useEffect, useState, useContext } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { appUrl } from '../api/endpoints';

import { LanguageContext } from '../context/LanguageContext';
import planTranslations from '../languages/plan.json';
import plansExtraTranslations from '../languages/plansExtra.json';
import './Plans.css'; 

const planExtraConfig = {
  basic: [
    { key: 'prioritySupport', enabled: false },
    { key: 'customAutomations', enabled: false },
  ],
  enterprise: [
    { key: 'emailSupport', enabled: true },
    { key: 'customAutomations', enabled: true },
    { key: 'phoneSupport', enabled: false },
  ],
  business: [
    { key: 'prioritySupport', enabled: true },
    { key: 'clientCommunication', enabled: true },
    { key: 'customReports', enabled: true },
  ],
};

export default function Plans() {
  const { lang } = useContext(LanguageContext);
  const baseT = planTranslations[lang] || {};
  const extraT = plansExtraTranslations[lang] || {};
  const t = {
    ...baseT,
    ...extraT,
    planTagline: {
      ...(baseT.planTagline || {}),
      ...(extraT.planTagline || {}),
    },
    extraFeatures: {
      ...(baseT.extraFeatures || {}),
      ...(extraT.extraFeatures || {}),
    },
  };

  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hoveredPlanId, setHoveredPlanId] = useState(null);
  const [selectedPlanId, setSelectedPlanId] = useState(null);

  const navigate = useNavigate();

  useEffect(() => {
    axios
      .get(appUrl('payment/plans'))
      .then((res) => {
        const data = res.data || [];
        setPlans(data);

        setSelectedPlanId((prev) => {
          if (prev) return prev;
          const enterprise = data.find(
            (p) => p.name && p.name.toLowerCase() === 'enterprise'
          );
          if (enterprise) return enterprise.plan_id;
          return data[0]?.plan_id ?? null;
        });

        setLoading(false);
      })
      .catch((err) => {
        console.error('Error fetching plans:', err);
        setPlans([]);
        setLoading(false);
      });
  }, []);

  if (loading) {
    return (
      <div className='plans-loading-wrapper'>
        <span className='plans-loading-text'>{t.loadingPlans}</span>
      </div>
    );
  }

  const capitalize = (str) =>
    str.charAt(0).toUpperCase() + str.slice(1);

  const localeByLanguage = {
    en: 'en-US',
    ar: 'ar-AE',
    ur: 'ur-PK',
  };

  const formatPlanPrice = (priceInMinorUnits, currencyCode = 'AED') => {
    const normalizedPrice = Number(priceInMinorUnits);
    const majorAmount = Number.isFinite(normalizedPrice) ? normalizedPrice / 100 : 0;
    const hasFraction = Number.isFinite(normalizedPrice) && normalizedPrice % 100 !== 0;

    return new Intl.NumberFormat(localeByLanguage[lang] || 'en-US', {
      style: 'currency',
      currency: String(currencyCode || 'AED').toUpperCase(),
      minimumFractionDigits: hasFraction ? 2 : 0,
      maximumFractionDigits: 2,
    }).format(majorAmount);
  };

  const getSupportLabel = (name) =>
    t.support[name] || t.support.default;

  const handleCheckout = (plan) => {
    const planCurrency = String(plan.currency || 'aed').toLowerCase();
    const priceLabel = formatPlanPrice(plan.price, plan.currency);
    const checkoutLabel = (t.checkoutLabel || '{planName} - {price}{unit}')
      .replace('{planName}', capitalize(plan.name))
      .replace('{price}', priceLabel)
      .replace('{unit}', ` ${t.unitPerMonth}`);

    navigate('/administration/payment', {
      state: {
        amount: Number(plan.price) || 0,
        currency: planCurrency,
        plan_id: plan.plan_id,
        plan_name: capitalize(plan.name),
        label: checkoutLabel,
      },
    });
  };

  const englishPlural = (count) => (count > 1 ? 's' : '');

  const whatYouWillGet = t.whatYouWillGet;

  return (
    <div className='plans-wrapper'>
      <div className='plans-inner-card'>
        <div className='plans-top-row'>
          <button type="button" className='plans-flexible-badge'>
            <span className='plans-flexible-dot' />
            <span className='plans-flexible-text'>
              {t.flexiblePricing}
            </span>
          </button>
        </div>

        <header className='plans-header'>
          <h1 className='plans-title'>{t.plansPageTitle}</h1>
          {t.plansPageSubtitle && (
            <p className='plans-subtitle'>{t.plansPageSubtitle}</p>
          )}
        </header>

        <div className='plans-card-row'>
          {plans.map((plan) => {
            const normalizedPrice = Number(plan.price) || 0;
            const priceString = formatPlanPrice(plan.price, plan.currency);

            const suffix =
              lang === 'en' ? englishPlural(plan.max_devices) : '';

            const baseFeatures = [
              t.features.devices
                .replace('{count}', plan.max_devices)
                .replace('{plural}', suffix),
              t.features.templates
                .replace('{count}', plan.max_templates)
                .replace(
                  '{plural}',
                  lang === 'en'
                    ? englishPlural(plan.max_templates)
                    : ''
                ),
              t.features.streamsPerTemplate
                .replace(
                  '{count}',
                  plan.max_datastream_per_template
                )
                .replace(
                  '{plural}',
                  lang === 'en'
                    ? englishPlural(
                        plan.max_datastream_per_template
                      )
                    : ''
                ),
              getSupportLabel(plan.name),
            ].map((label) => ({ label, enabled: true }));

            const extrasConfig =
              planExtraConfig[plan.name?.toLowerCase()] || [];

            const extraFeatures =
              (t.extraFeatures &&
                extrasConfig
                  .map(({ key, enabled }) => {
                    const txt = t.extraFeatures[key];
                    if (!txt) return null;
                    return { label: txt, enabled };
                  })
                  .filter(Boolean)) ||
              [];

            const allFeatures = [...baseFeatures, ...extraFeatures];

            const isSelected = selectedPlanId === plan.plan_id;
            const isHovered = hoveredPlanId === plan.plan_id;

            const showFreeWord = normalizedPrice === 0 && t.freeLabel;
            
            const cardClassName = `plans-plan-card ${isSelected ? 'plans-plan-card-selected' : 'plans-plan-card-side'} ${isHovered ? 'plans-plan-card-hover' : ''}`;
            const innerClassName = `plans-plan-inner ${isSelected ? 'plans-plan-inner-selected' : 'plans-plan-inner-side'}`;
            const spotClassName = `plans-card-spot ${isSelected ? 'plans-card-spot-selected' : 'plans-card-spot-side'}`;
            const iconOuterClassName = `plans-plan-icon-outer ${isSelected ? 'plans-plan-icon-outer-selected' : 'plans-plan-icon-outer-unselected'}`;
            const iconInnerClassName = `plans-plan-icon-inner ${isSelected ? 'plans-plan-icon-inner-selected' : 'plans-plan-icon-inner-unselected'}`;
            const buttonClassName = `plans-button-base ${isSelected ? 'plans-button-primary' : 'plans-button-side'}`;


            return (
              <div
                key={plan.plan_id}
                className={cardClassName}
                onMouseEnter={() => setHoveredPlanId(plan.plan_id)}
                onMouseLeave={() => setHoveredPlanId(null)}
                onClick={() => setSelectedPlanId(plan.plan_id)}
              >
                <div className={innerClassName}>
                  <div className={spotClassName} />

                  <div className='plans-card-content'>
                    <div className='plans-plan-header'>
                      <div className={iconOuterClassName}>
                        <div className={iconInnerClassName} />
                      </div>
                      <div className='plans-plan-header-text'>
                        <h2 className='plans-plan-name'>
                          {capitalize(plan.name)}
                        </h2>
                        {t.planTagline &&
                          t.planTagline[plan.name?.toLowerCase()] && (
                            <p className='plans-plan-tagline'>
                              {t.planTagline[plan.name?.toLowerCase()]}
                            </p>
                          )}
                      </div>
                    </div>

                    <div className='plans-price-block'>
                      {showFreeWord ? (
                        <p className='plans-price-free'>
                          {showFreeWord}
                        </p>
                      ) : (
                        <p className='plans-plan-price'>
                          <span className='plans-price-value'>
                            {priceString}
                          </span>
                          <span className='plans-price-unit'>
                            {' '}
                            {t.unitPerMonth}
                          </span>
                        </p>
                      )}
                    </div>

                    <div className='plans-button-wrapper'>
                      <button
                        className={buttonClassName}
                        onClick={(e) => {
                          e.stopPropagation(); 
                          handleCheckout(plan);
                        }}
                      >
                        {t.choosePlan.replace(
                          '{planName}',
                          capitalize(plan.name)
                        )}
                      </button>
                    </div>

                    <div className='plans-divider' />

                    <div className='plans-features-block'>
                      <p className='plans-features-title'>
                        {whatYouWillGet}
                      </p>
                      <ul className='plans-feature-list'>
                        {allFeatures.map(
                          ({ label, enabled }, i) => (
                            <li
                              key={i}
                              className={`plans-feature-item ${!enabled ? 'plans-feature-item-disabled' : ''}`}
                            >
                              <span
                                className={`plans-bullet-dot ${!enabled ? 'plans-bullet-dot-disabled' : ''}`}
                              />
                              <span>{label}</span>
                            </li>
                          )
                        )}
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
